import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import * as settingsApi from '@/api/settings'
import type { AppSettings } from '@/types'

export function useAppSettings(): AppSettings | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.settings(),
    queryFn: settingsApi.fetchSettings,
  })
  return data
}

/**
 * Optimistic: the change shows at once, the save runs in the background, and a failed save
 * rolls the screen back.
 */
export function useUpdateSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: settingsApi.updateSettings,
    onMutate: async (patch: Partial<AppSettings>) => {
      await qc.cancelQueries({ queryKey: queryKeys.settings() })
      const previous = qc.getQueryData<AppSettings>(queryKeys.settings())
      if (previous) qc.setQueryData<AppSettings>(queryKeys.settings(), { ...previous, ...patch })
      return { previous }
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.settings(), ctx.previous)
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: queryKeys.settings() }),
  })
}
